/* ═══════════════════════════════════════════════════
   RFP Preview Modal — now with "See Creator View" toggle
   per row so brand reps (and investors) can see exactly
   what each invited creator sees inside their inbox.
   ═══════════════════════════════════════════════════ */
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Send, Loader2, Check, Users, Eye, ChevronDown, ChevronUp, MapPin, ShieldCheck, RotateCw, XCircle } from 'lucide-react';
import { T, P, BG, CARD, BORDER } from './constants';
import CreatorKpiPreview from './CreatorKpiPreview';

// B2 — pre-send gate panel. Renders the five preflight checks green/red
// with their human-readable reasons; the Send button stays disabled
// until all pass.
function PreflightPanel({ preflight, loading, onRerun }) {
  return (
    <div className="rounded-xl px-3 py-2.5 mb-2" style={{ background: BG, border: `1px solid ${BORDER}` }} data-testid="preflight-panel">
      <div className="flex items-center gap-2 mb-1.5">
        <ShieldCheck className="h-3.5 w-3.5" style={{ color: preflight?.all_passed ? '#4ade80' : T }} />
        <p className="text-[10px] uppercase tracking-widest font-black" style={{ color: preflight?.all_passed ? '#4ade80' : T }}>
          Preflight {loading ? '· running…' : preflight?.all_passed ? '· all clear' : preflight ? '· blocked' : ''}
        </p>
        <button onClick={onRerun} disabled={loading} className="ml-auto text-[9px] font-bold flex items-center gap-1 text-gray-500 hover:text-white transition-colors disabled:opacity-40" data-testid="preflight-rerun-btn">
          <RotateCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} /> Re-run
        </button>
      </div>
      {loading && !preflight && <p className="text-[10px] text-gray-500">Checking wallet, creator identities, platform coverage…</p>}
      {preflight && (
        <div className="space-y-1" data-testid="preflight-checks">
          {(preflight.checks || []).map((c) => (
            <div key={c.id} className="flex items-start gap-2" data-testid={`preflight-check-${c.id}`}>
              {c.ok
                ? <Check className="h-3 w-3 mt-0.5 shrink-0 text-green-400" />
                : <XCircle className="h-3 w-3 mt-0.5 shrink-0 text-[#FF6B6B]" />}
              <p className="text-[10px] leading-snug">
                <span className={`font-bold ${c.ok ? 'text-gray-300' : 'text-[#FF6B6B]'}`}>{c.label}</span>
                <span className="text-gray-500 ml-1.5">{c.reason}</span>
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function RfpPreviewModal({
  open, onOpenChange, rfpLoading, rfpError, rfpSent, rfpSending,
  generatedRfps, onSendRfps, waveId,
  footprintPulled = false, footprintSummary = '',
  preflight = null, preflightLoading = false, onRunPreflight,
}) {
  const [expandedCreator, setExpandedCreator] = useState(null);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl text-white" style={{ background: CARD, borderColor: BORDER }}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-white"><Send className="h-5 w-5" style={{ color: T }} />RFP Preview</DialogTitle>
          <DialogDescription className="text-gray-500">{rfpLoading ? 'Generating AI-personalized briefs...' : `${generatedRfps.length} briefs generated. All include #ad.`}</DialogDescription>
        </DialogHeader>

        {/* iter 78: Footprint-aware indicator — shows the brand the LLM
            pulled their uploaded geographic context into every caption. */}
        {!rfpLoading && footprintPulled && (
          <div
            className="rounded-xl px-3 py-2.5 flex items-start gap-2.5 mb-2"
            style={{ background: 'rgba(0,212,255,0.08)', border: '1px solid rgba(0,212,255,0.25)' }}
            data-testid="footprint-context-indicator"
          >
            <MapPin className="h-3.5 w-3.5 text-[#00D4FF] flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-widest font-black text-[#00D4FF] mb-0.5">
                Drawing from your footprint
              </p>
              <p className="text-[11px] text-gray-300 leading-snug">
                {footprintSummary || "Your uploaded geographic + customer signature is informing every caption."}
              </p>
            </div>
          </div>
        )}
        {rfpLoading && (
          <div className="flex flex-col items-center py-12" data-testid="rfp-generating-spinner">
            <Loader2 className="h-10 w-10 animate-spin" style={{ color: T }} />
            <p className="text-sm text-gray-400 mt-4">Generating briefs...</p>
          </div>
        )}
        {rfpError && !rfpLoading && (
          <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl" data-testid="rfp-error">
            <p className="text-sm text-red-400">{rfpError}</p>
          </div>
        )}
        {!rfpLoading && generatedRfps.length > 0 && (
          <>
            <div className="space-y-3 max-h-[480px] overflow-y-auto py-3" data-testid="rfp-preview-list">
              {generatedRfps.map((rfp, i) => {
                const expanded = expandedCreator === rfp.creator_handle;
                const assignmentId = rfp.assignment_id; // populated once wave is sent
                return (
                  <div key={`${rfp.creator_handle}-${i}`} className="rounded-xl" style={{ background: BG, border: `1px solid ${BORDER}` }}>
                    <div className="p-4 flex gap-4">
                      {rfp.avatar ? (
                        <img src={rfp.avatar} alt="" className="w-12 h-12 rounded-full object-cover flex-shrink-0" />
                      ) : (
                        <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: `${T}20` }}>
                          <Users className="h-5 w-5" style={{ color: T }} />
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-bold text-white">{rfp.creator_name}</span>
                          <span className="text-xs" style={{ color: T }}>{rfp.creator_handle}</span>
                          {waveId && assignmentId && (
                            <button
                              onClick={() => setExpandedCreator(expanded ? null : rfp.creator_handle)}
                              className="ml-auto text-[10px] font-bold flex items-center gap-1 px-2 py-1 rounded-full transition-all hover:scale-[1.03]"
                              style={{ background: `${P}18`, color: P, border: `1px solid ${P}40` }}
                              data-testid={`see-creator-view-${rfp.creator_handle}`}>
                              <Eye className="h-3 w-3" />
                              {expanded ? 'Hide' : 'See Creator View'}
                              {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                            </button>
                          )}
                        </div>
                        <p className="text-xs text-gray-400">{rfp.caption}</p>
                        <div className="flex gap-2 mt-2">
                          <span className="text-[9px] px-2 py-0.5 rounded-full font-semibold" style={{ background: `${P}15`, color: P, border: `1px solid ${P}30` }}>#ad</span>
                          <span className="text-[9px] px-2 py-0.5 rounded-full bg-green-500/10 text-green-400 border border-green-500/20 font-semibold">FTC Compliant</span>
                        </div>
                      </div>
                    </div>
                    {expanded && waveId && assignmentId && (
                      <div className="px-4 pb-4">
                        <CreatorKpiPreview waveId={waveId} assignmentId={assignmentId} compact />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="pt-3" style={{ borderTop: `1px solid ${BORDER}` }}>
              {!rfpSent && (
                <PreflightPanel preflight={preflight} loading={preflightLoading} onRerun={() => onRunPreflight?.(waveId)} />
              )}
              {rfpSent ? (
                <div className="text-center py-3" data-testid="rfp-sent-confirmation">
                  <Check className="h-8 w-8 text-green-400 mx-auto mb-2" />
                  <p className="text-sm font-bold text-green-400">RFPs sent to {generatedRfps.length} creators!</p>
                </div>
              ) : (
                <button onClick={onSendRfps} disabled={rfpSending || preflightLoading || !preflight?.all_passed} className="w-full px-6 py-3.5 rounded-xl text-sm font-bold text-white flex items-center justify-center gap-2 disabled:opacity-50" style={{ background: P, boxShadow: `0 0 20px ${P}30` }} data-testid="send-rfps-btn">
                  {rfpSending
                    ? <><Loader2 className="h-4 w-4 animate-spin" />Sending...</>
                    : preflightLoading
                      ? <><Loader2 className="h-4 w-4 animate-spin" />Running preflight...</>
                      : !preflight?.all_passed
                        ? <><Send className="h-4 w-4" />Resolve preflight to send</>
                        : <><Send className="h-4 w-4" />Confirm &amp; Fire Wave to {generatedRfps.length} Creators (debits now)</>}
                </button>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
